<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-box-seam me-2"></i>{{ supplier.name }} Products
      </h4>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
        <button
          v-if="profile.role !== 'user'"
          class="btn btn-primary"
          @click="addProduct">
          <i class="bi bi-plus-circle me-1"></i> Add Product
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading products...</p>
      </div>

      <!-- Products Content -->
      <div v-if="!loading" class="products-content fade-in">
        <!-- Products Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ filteredProducts.length }}</span>
            <span class="text-muted">Products</span>
          </div>
          <UInput v-model="dataTableSearch" size="md" placeholder="Search product" class="mb-0 w-auto" />
        </div>

        <!-- Products Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
              <tr>
                <th>SKU</th>
                <th>CODE</th>
                <th>NAME</th>
                <th class="text-end">BUYING PRICE</th>
                <th class="text-end">SELLING PRICE</th>
                <th>STOCK</th>
                <th>REORDER</th>
                <th>R. STATUS</th>
                <th>STATUS</th>
                <th class="text-end">ACTIONS</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(product, index) in paginatedProducts" :key="product.id" class="product-row" :class="{'bg-light': index % 2 === 0}">
                <td>{{ product.sku }}</td>
                <td>{{ product.code }}</td>
                <td>{{ product.name }}</td>
                <td class="text-end">{{ formatNumber(product.buy) }}</td>
                <td class="text-end">{{ formatNumber(product.sell) }}</td>
                <td>{{ product.stock }} {{ product.unit.name }}</td>
                <td>{{ product.reorder }} {{ product.unit.name }}</td>
                <td>
                  <Badge pill :type="getPillByReorder(product.reorder < product.stock ? 'good' : 'reorder')">
                    {{ product.reorder < product.stock ? 'good' : 'reorder' }}
                  </Badge>
                </td>
                <td>
                  <Badge pill :type="getPillByStatus(product.status)">
                    {{ product.status }}
                  </Badge>
                </td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-primary" @click="productDetails(product.id)">
                    <i class="bi bi-eye"></i> Details
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import formatNumber from "@/utils/formatNumber";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import supplierService from "@/api/supplierService";
import Pagination from "@/components/pagination/Pagination.vue";
import Badge from "@/components/badge/Badge";

export default {
  name: "ProductsBySupplier",

  components: {
    Pagination,
    Badge
  },

  data(){
    return {
      profile: {},
      loading: true,
      dataTable: [],
      supplier: {},
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
      subscription: {
        package: {}
      },
    }
  },

  methods:{
    formatNumber,
    getPillByReorder(cellData) {
      switch (cellData) {
        case 'reorder':
          return 'danger';
        case 'good':
          return 'success';
      }
    },
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'inactive':
          return 'danger';
        case 'active':
          return 'success';
      }
    },
    async getProducts() {
      try {
        this.supplier = await supplierService.getSupplier(this.$route.params.id);
        this.dataTable = await supplierService.getProductsBySupplier(this.$route.params.id);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        this.loading = false;
      }
    },
    productDetails(id) {
      router.push('/product/' + id)
    },
    addProduct(){
      router.push('/add-product')
    },
  },

  computed: {
    filteredProducts(){
      if (!this.dataTableSearch) return this.dataTable;
      const term = this.dataTableSearch.toLowerCase();
      return this.dataTable.filter(p =>
          (p.code && p.code.toLowerCase().includes(term)) ||
          (p.name && p.name.toLowerCase().includes(term)) ||
          (p.sku && p.sku.toLowerCase().includes(term))
      );
    },
    paginatedProducts(){
      const start = (this.dataTablePage - 1) * this.dataTablePerPage;
      return this.filteredProducts.slice(start, start + this.dataTablePerPage);
    },
    totalPages(){
      return Math.ceil(this.filteredProducts.length / this.dataTablePerPage) || 1;
    },
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getProducts()
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },
}
</script>

<style scoped>
/* Products page styles */
.products-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.product-row {
  transition: all 0.2s ease;
}

.product-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table th,
  .table td {
    padding: 0.75rem 1rem;
  }
}
</style>
